import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ContentReport } from '@/hooks/useContentModeration';
import { useModerationActions, UserModerationHistory } from '@/hooks/useModerationActions';
import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle, Ban, Trash2, Shield, Clock } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { useEffect } from 'react';

interface ModerationActionDialogProps {
  report: ContentReport | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const categoryColors: Record<string, string> = {
  hate_speech: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
  harassment: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400',
  spam: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
  sexual_content: 'bg-pink-100 text-pink-800 dark:bg-pink-900/20 dark:text-pink-400',
  violence: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
  misinformation: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400',
  other: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400',
};

export function ModerationActionDialog({
  report,
  open,
  onClose,
  onSuccess,
}: ModerationActionDialogProps) {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [suspensionDays, setSuspensionDays] = useState('7');
  const [userHistory, setUserHistory] = useState<UserModerationHistory | null>(null);
  const { executeModerationAction, getUserModerationHistory, loading } = useModerationActions();

  useEffect(() => {
    if (report && open) {
      const contentUserId = report.content?.user_id || report.content?.sender_id || report.content?.author?.id;
      if (contentUserId) {
        getUserModerationHistory(contentUserId).then(setUserHistory);
      }
      setReason(report.report_category.replace('_', ' '));
    }
  }, [report, open]);

  if (!report) return null;

  const contentUserId = report.content?.user_id || report.content?.sender_id;
  const getContentText = () => {
    if (!report.content) return '[Content not available]';
    return report.content.comment_text || report.content.text || report.content.review_text || '[No text content]';
  };

  const handleAction = async (actionType: 'issue_warning' | 'remove_content' | 'suspend_user' | 'remove_and_warn') => {
    if (!contentUserId) {
      alert('Cannot execute action: User ID not found');
      return;
    }

    let params: any = {
      action: actionType,
      reportId: report.id,
      userId: contentUserId,
      reason: reason || report.report_category,
      details: details || undefined,
    };

    if (actionType === 'remove_content' || actionType === 'remove_and_warn') {
      params.contentType = report.content_type;
      params.contentId = report.content_id;
    }

    if (actionType === 'suspend_user') {
      const suspensionType = suspensionDays === 'permanent' ? 'permanent' : 'temporary';
      params.suspensionType = suspensionType;
      if (suspensionType === 'temporary') {
        params.durationDays = parseInt(suspensionDays);
      }
    }

    const result = await executeModerationAction(params);
    
    if (result.success) {
      onSuccess();
      onClose();
    }
  };

  const handleDismiss = async () => {
    // Simple dismiss without enforcement
    const dismissReason = reason || 'Report reviewed - no action needed';
    await handleAction('issue_warning' as any); // This will just log but we'll update the report
    onSuccess();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Moderation Action Required</DialogTitle>
          <DialogDescription>
            Review the report and choose an appropriate enforcement action
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="report" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="report">Report Details</TabsTrigger>
            <TabsTrigger value="history">User History</TabsTrigger>
          </TabsList>

          <TabsContent value="report" className="space-y-4">
            {/* Reporter Info */}
            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
              <Avatar className="h-10 w-10">
                <AvatarImage src={report.reporter.image || undefined} />
                <AvatarFallback>
                  {report.reporter.username?.[0]?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="font-medium">@{report.reporter.username}</p>
                <p className="text-sm text-muted-foreground">
                  Reported {formatDistanceToNow(new Date(report.created_at), { addSuffix: true })}
                </p>
              </div>
              <Badge className={categoryColors[report.report_category] || categoryColors.other}>
                {report.report_category.replace('_', ' ')}
              </Badge>
            </div>

            {/* Reporter's Description */}
            {report.additional_details && (
              <div>
                <Label className="text-sm font-medium">Reporter's Notes:</Label>
                <p className="mt-1 text-sm text-muted-foreground p-3 bg-muted rounded-lg">
                  {report.additional_details}
                </p>
              </div>
            )}

            {/* Reported Content */}
            <div>
              <Label className="text-sm font-medium">Reported Content:</Label>
              <div className="mt-1 p-4 bg-destructive/10 rounded-lg border-l-4 border-destructive">
                <p className="text-sm whitespace-pre-wrap">{getContentText()}</p>
                {report.content?.author && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-destructive/20">
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={report.content.author.image || undefined} />
                      <AvatarFallback>
                        {report.content.author.username?.[0]?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <p className="text-xs text-muted-foreground">
                      Posted by @{report.content.author.username}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Action Inputs */}
            <div className="space-y-3">
              <div>
                <Label htmlFor="reason">Action Reason</Label>
                <Input
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Brief reason for action..."
                />
              </div>
              
              <div>
                <Label htmlFor="details">Additional Details (Optional)</Label>
                <Textarea
                  id="details"
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  placeholder="Add any additional notes..."
                  rows={3}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="history" className="space-y-4">
            {userHistory ? (
              <>
                {/* Strike Counter */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Active Strikes (90 days)</p>
                        <p className="text-2xl font-bold">{userHistory.strikes} / 3</p>
                      </div>
                      <AlertTriangle className={`w-8 h-8 ${userHistory.strikes >= 2 ? 'text-destructive' : 'text-yellow-500'}`} />
                    </div>
                  </CardContent>
                </Card>

                {/* Active Suspensions */}
                {userHistory.suspensions.filter(s => s.status === 'active').length > 0 && (
                  <div>
                    <Label className="text-sm font-medium">Active Suspensions:</Label>
                    <div className="mt-2 space-y-2">
                      {userHistory.suspensions.filter(s => s.status === 'active').map((suspension) => (
                        <div key={suspension.id} className="p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                          <div className="flex items-center gap-2 mb-1">
                            <Ban className="w-4 h-4 text-destructive" />
                            <p className="font-medium text-sm">
                              {suspension.suspension_type === 'permanent' ? 'Permanently Banned' : 'Temporarily Suspended'}
                            </p>
                          </div>
                          <p className="text-xs text-muted-foreground">{suspension.reason}</p>
                          {suspension.expires_at && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Expires: {new Date(suspension.expires_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Actions */}
                {userHistory.recentActions.length > 0 && (
                  <div>
                    <Label className="text-sm font-medium">Recent Moderation Actions:</Label>
                    <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                      {userHistory.recentActions.map((action, idx) => (
                        <div key={idx} className="p-2 bg-muted rounded text-xs">
                          <div className="flex justify-between items-start">
                            <p className="font-medium">{action.action_type.replace('_', ' ')}</p>
                            <p className="text-muted-foreground">
                              {formatDistanceToNow(new Date(action.created_at), { addSuffix: true })}
                            </p>
                          </div>
                          <p className="text-muted-foreground mt-1">{action.reason}</p>
                          {action.moderator && (
                            <p className="text-muted-foreground mt-1">By: @{action.moderator.username}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-center text-muted-foreground py-4">Loading user history...</p>
            )}
          </TabsContent>
        </Tabs>

        {report.status === 'pending' && (
          <DialogFooter className="flex-col gap-2">
            <div className="flex flex-wrap gap-2 w-full">
              {/* Gentle Actions */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleDismiss}
                disabled={loading || !reason.trim()}
                className="flex-1"
              >
                <Shield className="w-4 h-4 mr-2" />
                Dismiss Report
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAction('issue_warning')}
                disabled={loading || !reason.trim()}
                className="flex-1"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Issue Warning
              </Button>

              {/* Content Removal */}
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleAction('remove_content')}
                disabled={loading || !reason.trim()}
                className="flex-1"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove Content
              </Button>

              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleAction('remove_and_warn')}
                disabled={loading || !reason.trim()}
                className="flex-1"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove + Warn
              </Button>
            </div>

            {/* Suspension Section */}
            <div className="w-full pt-2 border-t">
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label htmlFor="suspension-duration" className="text-xs">Suspension Duration</Label>
                  <Select value={suspensionDays} onValueChange={setSuspensionDays}>
                    <SelectTrigger id="suspension-duration">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 Day</SelectItem>
                      <SelectItem value="3">3 Days</SelectItem>
                      <SelectItem value="7">7 Days</SelectItem>
                      <SelectItem value="14">14 Days</SelectItem>
                      <SelectItem value="30">30 Days</SelectItem>
                      <SelectItem value="permanent">Permanent Ban</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <Button
                  variant="destructive"
                  onClick={() => handleAction('suspend_user')}
                  disabled={loading || !reason.trim()}
                >
                  <Ban className="w-4 h-4 mr-2" />
                  {suspensionDays === 'permanent' ? 'Ban User' : 'Suspend User'}
                </Button>
              </div>
              
              {userHistory && userHistory.strikes >= 2 && (
                <p className="text-xs text-destructive mt-2">
                  ⚠️ Warning: User has {userHistory.strikes} strikes. Another warning will trigger automatic 7-day suspension.
                </p>
              )}
            </div>
          </DialogFooter>
        )}

        {report.status === 'reviewed' && (
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-sm font-medium mb-2">This report has been reviewed</p>
            {report.review_notes && (
              <p className="text-sm text-muted-foreground">{report.review_notes}</p>
            )}
            {report.reviewer && (
              <p className="text-xs text-muted-foreground mt-2">
                Reviewed by @{report.reviewer.username}
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}